// «Parla col tuo quaderno»: una conversazione persistente (una richiesta lato server)
// in cui mamma fa domande o dice cosa non va. Le piccole cose il quaderno le sistema
// subito e le mostra come «fatto ✓» con un solo «↩️ Annulla»; il resto lo segna per
// il caro (bolla 📝). Cambiare schermata spegne il microfono e chiude la risposta in volo.
// Con `?prova=1` la schermata guida mamma in una prova senza rischi: cambia il titolo di una sua
// storia e glielo fa annullare, con bolle locali lette a voce (l'annuncio «Una cosa nuova» in Home).
import { api, ApiError, postSse } from '../lib/api.js';
import { deleteRecording } from '../lib/idb.js';
import { finishRecording, Recorder } from '../lib/recorder.js';
import { playUrl, stopSpeaking } from '../lib/tts.js';
import { el, quandoRelativo } from '../lib/ui.js';

type UI = {
  screen: (...nodes: Array<Node | string | null | undefined>) => void;
  testata: (title: string) => HTMLElement;
  onLeave: (cleanup: () => void) => void;
  speak: (text: string, opts?: { force?: boolean }) => Promise<void>;
  caro: string;
  /** Aggiorna il pallino delle novità (aprire la conversazione segna viste le risposte). */
  aggiornaNovita?: () => void;
};

// Contratto «Il quaderno che sistema le piccole cose» (v2.16): forme delle risposte.
type Ref = { kind: 'storia' | 'foto' | 'video' | 'persona' | 'spunto'; id: number; title?: string; storyId?: number };
type Azione = { id: number; messageId?: number; kind: string; label: string; undone: boolean; undoable: boolean; ref?: Ref };
type Messaggio = { id: number; author: string; kind?: string; text: string | null; mediaUrl?: string | null; at: string; delegata?: boolean };
// Le due o tre scelte che il caro le manda quando la cosa dipende dal suo gusto
// («dividiamo il ricettario in dolci e salati?»): la domanda è già una frase del filo.
type Scelta = { testo: string; spiega?: string };
// `prova`: «Tienilo / Torna com'era» dopo una modifica per delega — la risposta è del quaderno, non del caro.
type Domanda = { messageId: number; domanda: string; scelte: Scelta[]; sceltaFatta: number | null; prova?: boolean };
type Conversazione = {
  request?: { id: number; status?: string; title?: string | null; createdAt?: string; lastMessageAt?: string | null };
  messages?: Messaggio[];
  actions?: Azione[];
  domanda?: Domanda | null;
};
type Contesto = { storiaId?: number; fotoId?: number; chiedi?: 'quando' };
type Turno = { text: string } | { mediaId: number };

// L'id della conversazione resta sul tablet un giorno: se chiude l'app e torna
// dopo dieci minuti ritrova il «fatto ✓» di prima (sessionStorage morirebbe con la PWA).
const CONV_KEY = 'memorie_quaderno_conv';
const CONV_TTL_MS = 24 * 60 * 60 * 1000;
// Errori del turno dopo i quali si riparte con una conversazione nuova, ripetendo il messaggio.
const RIAPRI = new Set(['richiesta_chiusa', 'conversazione_lunga']);

function leggiConvSalvata(): number | null {
  try {
    const raw = localStorage.getItem(CONV_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as { id?: unknown; at?: unknown };
    if (typeof v.id !== 'number' || typeof v.at !== 'number' || Date.now() - v.at > CONV_TTL_MS) return null;
    return v.id;
  } catch { return null; }
}
function salvaConv(id: number | null): void {
  try {
    if (id === null) localStorage.removeItem(CONV_KEY);
    else localStorage.setItem(CONV_KEY, JSON.stringify({ id, at: Date.now() }));
  } catch { /* modalità privata */ }
}

// L'annuncio «Una cosa nuova» in Home e la prova guidata condividono questa chiave:
// assente → si mostra; {rimandato:<ts>} → riappare dopo tre giorni; {fatto:<ts>} → mai più.
export const ANNUNCIO_KEY = 'memorie_annuncio_quaderno';
const RIMANDO_MS = 3 * 24 * 60 * 60 * 1000;
export function annuncioDaMostrare(): boolean {
  try {
    const raw = localStorage.getItem(ANNUNCIO_KEY);
    if (!raw) return true;
    const v = JSON.parse(raw) as { rimandato?: unknown; fatto?: unknown };
    if (typeof v.fatto === 'number') return false;
    if (typeof v.rimandato === 'number') return Date.now() - v.rimandato > RIMANDO_MS;
    return true;
  } catch { return true; }
}
export function segnaAnnuncio(stato: 'rimandato' | 'fatto'): void {
  try { localStorage.setItem(ANNUNCIO_KEY, JSON.stringify({ [stato]: Date.now() })); } catch { /* modalità privata */ }
}

function idPositivo(v: string | null): number | null {
  const n = Number(v);
  return v && Number.isInteger(n) && n > 0 ? n : null;
}

function iconaAzione(kind: string): string {
  if (kind === 'story_date' || kind === 'photo_date') return '📅';
  if (kind === 'story_title' || kind === 'caption' || kind === 'video_caption') return '✏️';
  if (kind === 'video_hide' || kind === 'video_unhide') return '🎞';
  if (kind === 'text_layout') return '📝';
  if (kind === 'recipe' || kind === 'recipe_tipo') return '🍴';
  if (kind === 'photo_hide') return '🙈';
  if (kind === 'photo_unhide') return '👁';
  if (kind === 'audio_hide' || kind === 'audio_piece_hide') return '🙈';
  if (kind === 'audio_unhide' || kind === 'audio_piece_unhide') return '👂';
  if (kind === 'photo_attach' || kind === 'photo_move' || kind === 'photo_detach' || kind === 'photo_order' || kind === 'audio_move') return '🔀';
  if (kind === 'person_rename') return '👤';
  if (kind === 'person_years') return '🌳';
  if (kind === 'question_retire' || kind === 'question_restore') return '❓';
  if (kind === 'baule_hide' || kind === 'baule_unhide') return '🧳';
  return '✓';
}

function messaggioErrore(code: string, message: unknown, caro: string): string {
  switch (code) {
    case 'vuoto': return 'Non ho capito niente: riprova, oppure scrivimelo qui sotto.';
    case 'audio_non_valido': return 'Non sono riuscita a sentire la registrazione: riprova.';
    case 'occupato': return 'Sto ancora finendo la risposta di prima: aspetta un momento e riprova.';
    case 'strumenti_spenti': return `Per ora posso solo rispondere alle domande: le correzioni le segno per ${caro}.`;
    case 'interno': return 'Qualcosa non ha funzionato dalla mia parte: riprova tra un momento.';
    default: return typeof message === 'string' && message ? message : 'Qualcosa non ha funzionato.';
  }
}

function daEvento(d: Record<string, unknown>): Azione {
  const ref = d['ref'] && typeof d['ref'] === 'object' ? (d['ref'] as Ref) : undefined;
  return {
    id: Number(d['actionId'] ?? d['id']),
    kind: String(d['kind'] ?? ''),
    label: String(d['label'] ?? ''),
    undone: d['undone'] === true,
    undoable: d['undoable'] !== false,
    ...(ref && typeof ref.id === 'number' && typeof ref.kind === 'string' ? { ref } : {}),
  };
}

export async function renderQuaderno(ui: UI, params: URLSearchParams): Promise<void> {
  const chat = el('div', { class: 'chat' });
  const statoEl = el('p', { class: 'stato-ascolto', role: 'status' }, '');
  const sottotitolo = el('p', { class: 'data-oggi' }, '');
  sottotitolo.hidden = true;
  // Un solo abort per visita: chiude la risposta in volo quando lei cambia schermata.
  const ctrl = new AbortController();
  let occupato = false;
  let recorder: Recorder | null = null;
  let conversazioneId: number | null = null;
  let contesto: Contesto | null = null;
  // La prova guidata: bersaglio, a che punto siamo, cosa leggere a voce dopo la risposta.
  // Nessun timer agisce da solo: ogni passo aspetta un suo tocco.
  const prova = params.get('prova') === '1' ? { titolo: '', guidata: false, conclusa: false, daLeggere: null as string | null } : null;

  // Da dove arriva: una conversazione precisa (Novità/push), una storia o una foto
  // (si parte puliti, col contesto), oppure il filo salvato sul tablet.
  const pConv = idPositivo(params.get('conversazione'));
  const pStoria = idPositivo(params.get('storia'));
  const pFoto = idPositivo(params.get('foto'));
  if (prova) { /* parte pulita: il contesto è la storia scelta in avviaProva */ }
  else if (pConv !== null) conversazioneId = pConv;
  else if (pStoria !== null) contesto = { storiaId: pStoria, ...(params.get('chiedi') === 'quando' ? { chiedi: 'quando' as const } : {}) };
  else if (pFoto !== null) contesto = { fotoId: pFoto };
  else conversazioneId = leggiConvSalvata();

  const bolla = (classe: string, ...contenuto: Array<Node | string | null | undefined>): HTMLElement => {
    const b = el('div', { class: `bolla ${classe}` }, ...contenuto);
    chat.append(b);
    b.scrollIntoView({ block: 'end' });
    return b;
  };
  const etichettaMic = (testo: string, icona = '🎙️'): void => {
    micBtn.replaceChildren(el('span', { class: 'mic' }, icona), testo);
  };
  const mostraIntro = (): void => {
    sottotitolo.textContent = `Chiedimi quello che vuoi sulle tue storie, oppure dimmi cosa non va: una data, una foto, un nome. Le piccole cose le sistemo subito; per il resto avviso ${ui.caro}.`;
    sottotitolo.hidden = false;
  };
  const dimenticaConversazione = (): void => {
    conversazioneId = null;
    salvaConv(null);
  };
  // Il filo della prova non si riprende la volta dopo: si riparte puliti, senza il copione.
  const ricordaConv = (id: number): void => { if (!prova) salvaConv(id); };

  /* ---------- riferimenti, azioni, richieste ---------- */

  function linkRef(r: Ref, inAzione: boolean): HTMLElement | null {
    const titolo = r.title ? `«${r.title}»` : null;
    switch (r.kind) {
      case 'storia': return el('a', { class: 'btn-mini', href: `#storia/${r.id}` }, inAzione || !titolo ? '📖 Apri la storia' : `📖 ${titolo}`);
      case 'foto': return el('a', { class: 'btn-mini', href: r.storyId ? `#storia/${r.storyId}` : '#foto' }, inAzione || !titolo ? '🖼 Vedi la fotografia' : `🖼 ${titolo}`);
      // La storia, all'altezza del video (mai in riproduzione da solo).
      case 'video': return r.storyId
        ? el('a', { class: 'btn-mini', href: `#storia/${r.storyId}?video=${r.id}` }, inAzione || !titolo ? '🎞 Vedi il video' : `🎞 ${titolo}`)
        : null;
      case 'persona': return el('a', { class: 'btn-mini', href: `#persona/${r.id}` }, inAzione || !titolo ? '👤 Apri la scheda' : `👤 ${r.title}`);
      case 'spunto': return el('a', { class: 'btn-mini', href: '#spunti' }, '💬 Vai agli spunti');
      default: return null;
    }
  }
  function rigaRiferimenti(refs: Ref[], inAzione = false): HTMLElement | null {
    const link = refs.map((r) => linkRef(r, inAzione)).filter((n): n is HTMLElement => n !== null);
    return link.length > 0 ? el('div', { class: 'bolla-riferimenti' }, ...link) : null;
  }

  function segnaAnnullata(b: HTMLElement, testo = 'Annullato ✓ Tutto com’era prima.'): void {
    b.classList.add('annullata');
    b.querySelector('.btn-quieto')?.remove();
    const meta = b.querySelector('.filo-meta');
    if (meta) meta.textContent = testo;
  }

  // «Fatto ✓» con un solo «↩️ Annulla» (44 px dentro la bolla): niente «sei sicura?» prima.
  function bollaAzione(a: Azione): HTMLElement {
    const meta = el('div', { class: 'filo-meta' }, 'Fatto ✓');
    const b = bolla('bolla-app bolla-azione',
      el('div', { class: 'esito-azione' }, `${iconaAzione(a.kind)} ${a.label}`),
      a.ref ? rigaRiferimenti([a.ref], true) : null,
      meta,
    );
    b.dataset['actionId'] = String(a.id);
    if (a.undone) { segnaAnnullata(b); return b; }
    if (!a.undoable) return b;
    const annulla = el('button', { class: 'btn-quieto', click: async () => {
      try {
        await api.post(`/api/quaderno/azioni/${a.id}/annulla`);
        segnaAnnullata(b);
        provaDopoAnnullo('ok');
      } catch (err) {
        if (err instanceof ApiError && err.status === 409) {
          if (err.code === 'gia_annullata') { segnaAnnullata(b, 'Era già stata annullata ✓ Tutto com’era prima.'); provaDopoAnnullo('ok'); return; }
          annulla.remove();
          meta.textContent = `Non riesco più ad annullarla: qualcosa è cambiato nel frattempo. Se vuoi, dillo a ${ui.caro} qui sotto.`;
          provaDopoAnnullo('fallito');
          return;
        }
        meta.textContent = 'Non ci sono riuscita: riprova.';
      }
    } }, '↩️ Annulla');
    b.append(annulla);
    return b;
  }

  // Delegata = stanotte ci prova il quaderno: un tentativo, mai il risultato (il notturno può
  // ancora passarla al caro), e domattina la scelta è sua. Dire «ti risponderà {caro}» lì accanto
  // la contraddirebbe.
  function bollaRichiesta(motivo: string, delegata = false): HTMLElement {
    return bolla('bolla-app bolla-richiesta',
      el('div', {}, delegata
        ? `📝 Me la sono segnata per stanotte: se posso sistemarla da sola, domattina te la faccio vedere e decidi tu; se no la passo a ${ui.caro}.`
        : `📝 Ho segnato questa cosa per ${ui.caro}: ti risponderà qui, e la troverai anche nelle tue Novità.`),
      motivo ? el('div', { class: 'filo-meta' }, motivo) : null,
    );
  }

  function annulloDaEvento(actionId: number, label: string): void {
    const b = chat.querySelector<HTMLElement>(`[data-action-id="${actionId}"]`);
    if (b) segnaAnnullata(b);
    else bolla('bolla-nota', `↩️ Annullato: ${label || 'tutto com’era prima'}`);
  }

  /* ---------- «Come preferisci?»: le scelte che il caro le manda ---------- */

  const hoCapito = (testo: string, prova = false): string => (prova ? `Ho capito: «${testo}» ✓` : `Ho capito: «${testo}». Lo dico a ${ui.caro} ✓`);

  // Due o tre pulsanti grandi, uno sotto l'altro: un tocco solo, nessun «sei sicura?».
  // Una volta scelto non tornano più, neanche ricaricando la pagina: resta quello che ha detto.
  function bollaScelte(idConv: number, d: Domanda): void {
    const gia = typeof d.sceltaFatta === 'number' ? d.scelte[d.sceltaFatta] : null;
    if (gia) { bolla('bolla-app bolla-scelte', hoCapito(gia.testo, d.prova)); return; }
    const pila = el('div', { class: 'pila-scelte' });
    const coda = el('div', { class: 'filo-meta' }, 'Oppure dimmelo tu qui sotto, con parole tue.');
    const b = bolla('bolla-app bolla-scelte', el('div', { class: 'scelte-titolo' }, 'Come preferisci?'), pila, coda);
    const bottoni: HTMLButtonElement[] = [];

    const scegli = async (i: number, testo: string): Promise<void> => {
      for (const x of bottoni) x.disabled = true; // niente doppio invio mentre parte
      let risposta: string | null = null;
      try {
        // «Tienilo / Torna com'era»: il quaderno le risponde subito, con la sua frase.
        const r = await api.post<{ risposta?: string }>(`/api/quaderno/${idConv}/scelta`, { scelta: i });
        risposta = typeof r?.risposta === 'string' ? r.risposta : null;
      } catch (err) {
        // Se la scelta era già registrata (un altro tocco, un altro dispositivo) va bene così.
        if (!(err instanceof ApiError && err.code === 'scelta_gia_fatta')) {
          for (const x of bottoni) x.disabled = false;
          coda.textContent = 'Non sono riuscita a mandarlo: riprova tra un momento.';
          return;
        }
      }
      b.replaceChildren(hoCapito(testo, d.prova));
      if (risposta) {
        bolla('bolla-app', el('div', {}, risposta)).scrollIntoView({ block: 'end' });
        void ui.speak(risposta);
      } else {
        b.scrollIntoView({ block: 'end' });
      }
    };
    for (const [i, s] of d.scelte.slice(0, 3).entries()) {
      const btn = el('button', { class: 'btn-secondario', click: () => scegli(i, s.testo) },
        el('span', { class: 'scelta-testo' }, s.testo),
        s.spiega ? el('span', { class: 'scelta-spiega' }, s.spiega) : null,
      ) as HTMLButtonElement;
      bottoni.push(btn);
      pila.append(btn);
    }
    void ui.speak('Come preferisci?');
  }

  /* ---------- il filo ripreso ---------- */

  function renderMessaggio(m: Messaggio): void {
    const autore = m.author === 'notturno' ? 'quaderno' : m.author; // a mamma il notturno è il quaderno
    const testo = m.text ?? '';
    const ascolta = m.mediaUrl ? el('button', { class: 'btn-mini', 'data-ruolo': 'voce', click: () => void playUrl(m.mediaUrl!) }, autore === 'mamma' ? '▶  La tua voce' : '▶  Ascolta') : null;
    if (m.kind === 'nota_per_vincenzo') { bollaRichiesta(testo, m.delegata === true); return; }
    if (m.kind === 'annullo') { bolla('bolla-nota', `↩️ ${testo || 'Annullato: tutto com’era prima.'}`); return; }
    if (autore === 'mamma') {
      bolla('bolla-mamma', testo ? el('div', {}, testo) : ascolta ?? '…');
      return;
    }
    const chi = autore === 'vincenzo' ? ui.caro : 'il tuo quaderno';
    bolla(autore === 'vincenzo' ? 'bolla-caro' : 'bolla-app',
      el('div', {}, testo),
      ascolta,
      el('div', { class: 'filo-meta' }, `${chi} · ${quandoRelativo(m.at)}`),
    );
  }

  async function riprendiFilo(id: number): Promise<void> {
    let conv: Conversazione;
    try {
      conv = await api.get<Conversazione>(`/api/quaderno/${id}`);
    } catch (err) {
      if (ctrl.signal.aborted) return;
      if (err instanceof ApiError && err.status === 404) {
        dimenticaConversazione();
        bolla('bolla-nota', 'Non riesco a ritrovare la conversazione di prima: ricominciamo da qui.');
        mostraIntro();
        return;
      }
      bolla('bolla-nota', 'In questo momento non riesco a rileggere la conversazione di prima, ma quello che mi dici adesso arriva lo stesso.');
      return;
    }
    if (ctrl.signal.aborted) return;
    const idConv = typeof conv.request?.id === 'number' ? conv.request.id : id;
    conversazioneId = idConv;
    const messaggi = Array.isArray(conv.messages) ? conv.messages : [];
    const azioni = Array.isArray(conv.actions) ? conv.actions : [];
    const domanda = conv.domanda && Array.isArray(conv.domanda.scelte) && conv.domanda.scelte.length > 0 ? conv.domanda : null;
    let scelteMostrate = false;
    if (messaggi.length === 0 && azioni.length === 0) {
      mostraIntro();
    } else {
      const q = conv.request?.createdAt ? quandoRelativo(conv.request.createdAt) : '';
      // «del 18 febbraio 2015» ma «di ieri» / «di 3 giorni fa».
      bolla('bolla-nota', q ? `La nostra conversazione ${/^\d+ [a-zà]+ \d{4}$/.test(q) ? 'del' : 'di'} ${q}` : 'La nostra conversazione');
      const perMessaggio = new Map<number, Azione[]>();
      const senzaMessaggio: Azione[] = [];
      for (const a of azioni) {
        if (typeof a.messageId === 'number') perMessaggio.set(a.messageId, [...(perMessaggio.get(a.messageId) ?? []), a]);
        else senzaMessaggio.push(a);
      }
      for (const m of messaggi) {
        renderMessaggio(m);
        for (const a of perMessaggio.get(m.id) ?? []) bollaAzione(a);
        perMessaggio.delete(m.id);
        // I pulsanti stanno subito sotto la domanda, non in fondo a tutto.
        if (domanda && m.id === domanda.messageId) { bollaScelte(idConv, domanda); scelteMostrate = true; }
      }
      for (const resto of perMessaggio.values()) for (const a of resto) bollaAzione(a);
      for (const a of senzaMessaggio) bollaAzione(a);
    }
    if (domanda && !scelteMostrate) bollaScelte(idConv, domanda);
    if (conv.request?.status === 'closed') {
      bolla('bolla-nota', 'Questa conversazione è chiusa: se mi dici altro, ne apro una nuova.');
      dimenticaConversazione();
    } else {
      ricordaConv(conversazioneId);
    }
    ui.aggiornaNovita?.(); // il server ha appena segnato viste le risposte
  }

  // Apertura con contesto: bolla locale letta a voce, nessuna chiamata a Claude prima che parli.
  async function apriConContesto(c: Contesto): Promise<void> {
    let testo: string;
    if (c.storiaId) {
      const s = await api.get<{ title?: string | null }>(`/api/memories/${c.storiaId}`).catch(() => null);
      if (ctrl.signal.aborted) return;
      const dove = s?.title ? `in «${s.title}»` : 'in questa storia';
      // Arrivando da «Sai dirmi quando?» la domanda è già fatta: accoglierla con «dimmi cosa non
      // va» le farebbe pensare di aver sbagliato qualcosa, quando le si stava solo chiedendo una data.
      // Esempi che valgono per ogni istanza: il caro qui non ha nome né genere (per Simonetta è Martina).
      testo = c.chiedi === 'quando'
        ? `Di che anno è ${s?.title ? `«${s.title}»` : 'questo ricordo'}? Anche solo «negli anni Sessanta» o «quando andavo a scuola» va benissimo: ci penso io a metterla al suo posto.`
        : `Dimmi cosa non va ${dove}: la data, una fotografia, un nome sbagliato… Le piccole cose le sistemo subito, per il resto avviso ${ui.caro}.`;
    } else {
      const f = await api.get<{ memory_title?: string | null }>(`/api/photos/${c.fotoId}`).catch(() => null);
      if (ctrl.signal.aborted) return;
      testo = `Dimmi cosa non va in questa fotografia${f?.memory_title ? ` di «${f.memory_title}»` : ''}: la data, la didascalia, la storia a cui appartiene… Ci penso io.`;
    }
    bolla('bolla-app', testo);
    void ui.speak(testo);
  }

  /* ---------- la prova guidata (#chiedi?prova=1) ---------- */

  // Bolle locali con la voce del quaderno: niente chiamate a Claude finché lei non tocca «Invia».
  function bollaProva(testo: string, ...bottoni: HTMLElement[]): HTMLElement {
    const b = bolla('bolla-app bolla-prova', el('div', {}, testo), ...bottoni);
    stopSpeaking();
    void ui.speak(testo);
    return b;
  }

  // Bersaglio: la sua storia più recente. Senza storie si passa direttamente alla spiegazione.
  async function avviaProva(): Promise<void> {
    if (!prova) return;
    const recenti = await api.get<Array<{ id: number; title: string }>>('/api/memories/recent?limit=3').catch(() => null);
    if (ctrl.signal.aborted) return;
    // Rete assente: niente prova adesso, e l'annuncio in Home resta per la prossima volta.
    if (recenti === null) { bollaProva('In questo momento non riesco a leggere le tue storie: riproviamo un’altra volta, dalla pagina iniziale.'); return; }
    const storia = recenti[0];
    if (!storia) {
      concludiProva(`Per ora non c’è ancora una storia su cui provare. Quando ce ne sarà una, basterà dirmi cosa non va: una data, una fotografia, un nome. Le piccole cose le sistemo subito e puoi sempre annullare; quello che non posso fare da sola lo passo a ${ui.caro}, che ti risponde qui.`);
      return;
    }
    contesto = { storiaId: storia.id }; // così la storia è già «vista» dagli strumenti del quaderno
    prova.titolo = storia.title;
    proponiProva();
  }

  // La richiesta è già scritta nel campo: le basta «Invia» (o dirla a voce). Nessun fuoco automatico.
  function proponiProva(): void {
    if (!prova) return;
    prova.guidata = false;
    bollaProva('Proviamo insieme la cosa nuova, senza rischi. Ti ho già scritto qui sotto cosa chiedermi: tocca “Invia”, oppure dimmelo tu a voce.');
    ta.value = `Cambia il titolo della storia «${prova.titolo}» in «${prova.titolo} (prova)»`;
  }

  // È comparsa la riga «✓»: adesso il passo è annullarla. Il bottone si mette in luce.
  function provaDopoAzione(b: HTMLElement): void {
    if (!prova || prova.guidata || prova.conclusa) return;
    prova.guidata = true;
    b.querySelector('.btn-quieto')?.classList.add('attenzione');
    const testo = 'Visto? La riga con ✓ dice cosa ho fatto. Adesso tocca “↩️ Annulla” per rimettere tutto com’era.';
    bolla('bolla-app bolla-prova', el('div', {}, testo));
    prova.daLeggere = testo; // letta dopo la risposta del quaderno, non sopra
  }

  // Il turno è finito senza che il quaderno abbia fatto nulla: si può riprovare o lasciar stare.
  function provaNonRiuscita(): void {
    if (!prova || prova.guidata || prova.conclusa) return;
    const b = bollaProva('Non è andata come pensavo, capita. Vuoi riprovare? Altrimenti va bene lo stesso: funzionerà quando ti servirà davvero.',
      el('button', { class: 'btn-secondario', click: () => { b.querySelectorAll('button').forEach((x) => x.remove()); proponiProva(); } }, 'Riprova'),
      el('button', { class: 'btn-quieto', click: () => { segnaAnnuncio('fatto'); location.hash = '#home'; } }, 'Lascia stare'),
    );
  }

  // Annullato (o non annullabile: allora è il caro a rimetterlo dal suo pannello): la prova è fatta,
  // e resta fatta anche se se ne va senza toccare «Va bene, ho capito».
  function provaDopoAnnullo(esito: 'ok' | 'fallito'): void {
    if (!prova || prova.conclusa) return;
    segnaAnnuncio('fatto');
    concludiProva(esito === 'ok'
      ? `Brava! Funziona così anche per le date, le fotografie e i nomi. Quello che non posso fare da sola lo passo a ${ui.caro}, che ti risponde qui.`
      : `Stavolta non sono riuscita ad annullare, ma niente è perso: ${ui.caro} può rimettere il titolo com’era dal suo pannello, dove ogni mia correzione resta segnata. Funziona così anche per le date, le fotografie e i nomi: quello che non posso fare da sola lo passo a ${ui.caro}, che ti risponde qui.`);
  }

  function concludiProva(testo: string): void {
    if (!prova) return;
    prova.conclusa = true;
    prova.daLeggere = null;
    bollaProva(testo, el('button', { class: 'btn-primario', click: () => { segnaAnnuncio('fatto'); location.hash = '#home'; } }, 'Va bene, ho capito'));
  }

  /* ---------- il turno ---------- */

  async function assicuraConversazione(): Promise<number> {
    if (conversazioneId !== null) return conversazioneId;
    const r = await api.post<{ requestId: number }>('/api/quaderno', contesto ? { contesto } : {});
    if (typeof r?.requestId !== 'number') throw new Error('conversazione senza id');
    conversazioneId = r.requestId;
    ricordaConv(r.requestId);
    return r.requestId;
  }

  async function turno(body: Turno): Promise<void> {
    occupato = true;
    micBtn.disabled = true;
    invia.disabled = true;
    etichettaMic('Un momento…', '⏳');
    statoEl.textContent = 'Ci sto pensando…';
    // Le sue parole scritte compaiono subito; per la voce si aspetta la trascrizione.
    let bollaMamma = 'text' in body ? bolla('bolla-mamma', body.text) : null;
    // Appena il server conferma di aver ricevuto il messaggio (transcript) o ha già agito (azione),
    // il turno è «partito»: un errore dopo non deve far sparire la sua bolla né rimetterle il testo.
    let partito = false;
    try {
      for (let tentativo = 0; tentativo < 2; tentativo++) {
        const id = await assicuraConversazione();
        if (ctrl.signal.aborted) return;
        let bollaApp = null as HTMLElement | null;
        let risposta = '';
        let audioUrl = null as string | null;
        let errore = null as string | null;
        await postSse(`/api/quaderno/${id}/messaggi`, body, {
          transcript: (d) => { partito = true; if (!bollaMamma) bollaMamma = bolla('bolla-mamma', String(d['text'] ?? '')); },
          delta: (d) => {
            if (!bollaApp) bollaApp = bolla('bolla-app', '');
            // Una sola bolla di testo per turno: se nel frattempo è comparsa un'azione, scende sotto.
            else if (chat.lastElementChild !== bollaApp) chat.append(bollaApp);
            risposta += String(d['text'] ?? '');
            bollaApp.textContent = risposta;
            bollaApp.scrollIntoView({ block: 'end' });
          },
          azione: (d) => { partito = true; provaDopoAzione(bollaAzione(daEvento(d))); },
          annullo: (d) => { annulloDaEvento(Number(d['actionId']), String(d['label'] ?? '')); },
          nota: (d) => { bollaRichiesta(String(d['text'] ?? ''), d['delegata'] === true); },
          audio: (d) => { audioUrl = String(d['url'] ?? '') || null; },
          error: (d) => {
            errore = String(d['code'] ?? 'interno');
            if (!RIAPRI.has(errore)) bolla('bolla-nota', messaggioErrore(errore, d['message'], ui.caro));
          },
          done: (d) => {
            const t = String(d['text'] ?? risposta);
            if (t) {
              if (!bollaApp) bollaApp = bolla('bolla-app', t);
              else { bollaApp.textContent = t; if (chat.lastElementChild !== bollaApp) chat.append(bollaApp); }
            }
            risposta = t;
            const refs = Array.isArray(d['refs']) ? (d['refs'] as Ref[]) : [];
            const riga = bollaApp && refs.length > 0 ? rigaRiferimenti(refs) : null;
            if (bollaApp && riga) { bollaApp.append(riga); bollaApp.scrollIntoView({ block: 'end' }); }
          },
        }, { signal: ctrl.signal, timeoutMs: 75_000 }); // inattività: il server manda un :ping ogni 10 s
        if (ctrl.signal.aborted) return;
        if (errore && RIAPRI.has(errore)) {
          dimenticaConversazione();
          if (tentativo === 0) {
            bolla('bolla-nota', errore === 'richiesta_chiusa'
              ? 'Quella conversazione era già chiusa: ne ho aperta una nuova.'
              : 'La conversazione era diventata lunga: ne ho aperta una nuova.');
            continue;
          }
          bolla('bolla-nota', 'Non riesco ad aprire una conversazione nuova: riprova tra un momento.');
          if ('text' in body) ta.value = body.text;
          break;
        }
        statoEl.textContent = '';
        if (risposta) {
          if (audioUrl) await playUrl(audioUrl).catch(() => ui.speak(risposta));
          else await ui.speak(risposta);
        }
        if (prova?.daLeggere && !prova.conclusa) { const t = prova.daLeggere; prova.daLeggere = null; await ui.speak(t); }
        break;
      }
    } catch (err) {
      if (ctrl.signal.aborted) return; // ha cambiato schermata: silenzio
      statoEl.textContent = '';
      if (partito) {
        // Il messaggio è arrivato al quaderno (e magari ha già agito): si è persa solo la coda della risposta.
        bolla('bolla-nota', 'La risposta si è interrotta a metà, ma quello che mi hai detto è arrivato: se vuoi, chiedimi com’è andata.');
      } else {
        const vocale = !('text' in body);
        if (!vocale) { ta.value = body.text; bollaMamma?.remove(); } // le parole restano nel campo
        const dove = vocale ? 'Ripeti pure quando vuoi, oppure scrivimelo qui sotto.' : 'Le tue parole sono ancora qui sotto.';
        if (err instanceof ApiError && err.status === 404) {
          dimenticaConversazione();
          bolla('bolla-nota', `Non ritrovo più la conversazione di prima: ricominciamo da qui. ${dove}`);
        } else {
          bolla('bolla-nota', navigator.onLine === false
            ? `In questo momento il tablet non è collegato a internet. ${dove} Riprova tra un po’.`
            : err instanceof Error && err.message === 'tempo scaduto'
              ? `Ci ho messo troppo e mi sono persa: ${vocale ? 'ripeti pure quando vuoi' : 'riprova tra un momento, le tue parole sono qui sotto'}.`
              : `La rete ha fatto i capricci: riprova tra un momento. ${dove}`);
        }
      }
    } finally {
      occupato = false;
      if (statoEl.textContent === 'Ci sto pensando…') statoEl.textContent = '';
      micBtn.disabled = false;
      invia.disabled = false;
      etichettaMic('Parlami');
      if (conversazioneId !== null) ricordaConv(conversazioneId);
      if (!ctrl.signal.aborted) provaNonRiuscita(); // solo in prova, e solo se il turno non ha prodotto la riga «✓»
    }
  }

  /* ---------- microfono e tastiera ---------- */

  // Chiude la registrazione (dal tocco o dal silenzio) e manda la voce.
  async function chiudiTurno(): Promise<void> {
    if (!recorder || occupato) return;
    const rec = recorder;
    recorder = null;
    micBtn.classList.remove('registrando');
    // Finché la voce non è salvata e il turno non è finito, niente secondi tocchi né invii scritti.
    micBtn.disabled = true;
    invia.disabled = true;
    etichettaMic('Un momento…', '⏳');
    try {
      const { recordingId, durationS, chunkCount } = await rec.stop();
      if (ctrl.signal.aborted) return;
      if (durationS < 1) {
        statoEl.textContent = 'Non ho sentito nulla: riprova.';
        void deleteRecording(recordingId);
        return;
      }
      const { mediaId } = await finishRecording<{ mediaId: number }>(recordingId, { durationS, expectedChunks: chunkCount });
      void deleteRecording(recordingId);
      if (ctrl.signal.aborted) return;
      await turno({ mediaId });
    } catch {
      if (ctrl.signal.aborted) return;
      statoEl.textContent = 'Non sono riuscita a sentire quello che hai detto: riprova, oppure scrivilo qui sotto.';
    } finally {
      if (!occupato) { micBtn.disabled = false; invia.disabled = false; etichettaMic('Parlami'); }
    }
  }

  const micBtn = el('button', { class: 'pulsantone' }) as HTMLButtonElement;
  etichettaMic('Parlami');
  micBtn.addEventListener('click', async () => {
    if (occupato) return;
    if (recorder) { await chiudiTurno(); return; }
    stopSpeaking();
    recorder = new Recorder({
      onSilence: (s) => {
        // Le pause fanno parte del discorso: prima si avvisa, poi (con calma) si chiude da solo.
        if (s >= 6) { void chiudiTurno(); }
        else if (s >= 3.5) { statoEl.textContent = 'Se hai finito, tocca il pulsante — altrimenti continua pure, ti ascolto.'; }
      },
      onError: (msg) => { statoEl.textContent = msg; },
    });
    try {
      await recorder.start({ purpose: 'turn' });
      invia.disabled = true; // un turno scritto a metà registrazione lascerebbe il microfono acceso
      micBtn.classList.add('registrando');
      etichettaMic('Tocca quando hai finito');
      statoEl.textContent = 'Ti ascolto: dimmi pure.';
    } catch {
      recorder = null;
      statoEl.textContent = 'Non riesco a usare il microfono: scrivi qui sotto.';
    }
  });

  // La tastiera è un'alternativa di pari dignità: sempre visibile. Invio va a capo,
  // si manda solo col bottone; niente fuoco automatico (su iPad aprirebbe la tastiera).
  const ta = el('textarea', { class: 'scrivi-testo', rows: '3', placeholder: 'Oppure scrivi qui…', 'aria-label': 'Scrivi al quaderno' }) as HTMLTextAreaElement;
  const invia = el('button', { class: 'btn-verde' }, 'Invia ✉️') as HTMLButtonElement;
  invia.addEventListener('click', async () => {
    const text = ta.value.trim();
    if (!text || occupato) return;
    ta.value = '';
    await turno({ text });
  });

  ui.screen(
    ui.testata('Parla col tuo quaderno'),
    sottotitolo,
    chat,
    el('div', { class: 'zona-registra' }, micBtn, statoEl, el('div', { class: 'scrivi-area' }, ta, invia)),
  );
  ui.onLeave(() => {
    ctrl.abort();
    const rec = recorder;
    recorder = null;
    if (rec) void rec.discard().catch(() => undefined);
    stopSpeaking();
  });

  if (prova) await avviaProva();
  else if (conversazioneId !== null) await riprendiFilo(conversazioneId);
  else if (contesto) await apriConContesto(contesto);
  else mostraIntro();
}
